// API Client for DEFCON TERMINAL
// Handles all communication with Cloudflare Workers backend

import type {
  CreatePlayerRequest,
  CreatePlayerResponse,
  LoadPlayerRequest,
  LoadPlayerResponse,
  SaveGameRequest,
  SaveGameResponse,
  GameState,
  Decision,
} from '@shared/types';
import type {
  LeaderboardEntry,
  GlobalStatistics,
  ScoreBreakdown,
} from '@shared/types/gamestate';
import type {
  Campaign,
  CreateCampaignResponse,
  SaveCampaignRequest,
  CompleteScenarioRequest,
  CampaignCheckpoint,
  CampaignDecision,
} from '@shared/types/campaign';
import type { WorldState } from '@shared/types/worldstate';

// Use relative URL in development (proxied by Vite), full URL in production
const isDev = import.meta.env.DEV;
const API_BASE = isDev
  ? '/api'
  : 'https://defcon-terminal-api-production.avenislabs.workers.dev/api';

interface ApiError {
  error: string;
  code: string;
  status: number;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  errorCode?: string;
}

interface CompleteGameRequest {
  sessionToken: string;
  scenarioId: string;
  decisions: Decision[];
  playTimeSeconds: number;
  outcomeType: string;
  path: string;
  casualties?: number;
}

interface CompleteGameResponse {
  scenarioId: string;
  score: number;
  scoreBreakdown: ScoreBreakdown;
  rank: number;
  isPersonalBest: boolean;
  previousBest?: number;
}

interface LeaderboardResponse {
  entries: LeaderboardEntry[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

interface StatsResponse {
  global: GlobalStatistics;
  player?: {
    rank: number;
    percentile: number;
    statistics: import('@shared/types').PlayerStatistics;
  };
}

// Campaign-related interfaces
interface LoadCampaignResponse {
  campaign: Campaign;
  sessionToken: string;
  resumePoint?: {
    type: string;
    scenarioId?: string;
    nodeId?: string;
    description: string;
  };
}

interface SaveCampaignResponse {
  campaignUuid: string;
  savedAt: number;
  checkpointCreated?: boolean;
  checkpointUuid?: string;
}

interface CompleteScenarioResponse {
  campaignUuid: string;
  scenarioId: string;
  newWorldState: WorldState;
  unlockedScenarios?: string[];
  consequencesApplied?: string[];
  nextScenarioSuggestion?: string;
}

interface CheckpointData {
  scenarioId?: string;
  nodeId?: string;
  scenarioState?: unknown;
  worldState: WorldState;
  decisions: CampaignDecision[];
}

interface CheckpointResponse {
  checkpointUuid: string;
  name: string;
  createdAt: number;
  contextDescription: string;
}

interface CheckpointMetadata {
  checkpointUuid: string;
  name?: string;
  isAutoSave: boolean;
  contextDescription: string;
  scenarioId?: string;
  createdAt: number;
}

interface RestoreCheckpointResponse {
  campaign: Campaign;
  checkpoint: CampaignCheckpoint;
  sessionToken: string;
}

interface ConsequenceData {
  id: string;
  type: string;
  description: string;
  effects: Array<{
    target: string;
    modifier: string | number;
  }>;
  appliedAt?: number;
}

class ApiClient {
  private sessionToken: string | null = null;

  setSessionToken(token: string): void {
    this.sessionToken = token;
  }

  clearSessionToken(): void {
    this.sessionToken = null;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE}${endpoint}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...((options.headers as Record<string, string>) || {}),
    };

    if (this.sessionToken) {
      headers['Authorization'] = `Bearer ${this.sessionToken}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    let payload: ApiResponse<T> | T | null = null;
    try {
      payload = await response.json();
    } catch {
      // If the server returned OK but body isn't valid JSON, that's an error
      if (response.ok) {
        throw new Error(`API returned OK but response body is not valid JSON (${response.status})`);
      }
      payload = null;
    }

    if (!response.ok) {
      const errorPayload = payload as ApiResponse<unknown> | null;
      const error: ApiError = {
        error: errorPayload?.error || 'Unknown error',
        code: errorPayload?.errorCode || 'UNKNOWN',
        status: response.status,
      };
      throw new Error(`API Error: ${error.error} (${error.code})`);
    }

    if (payload && typeof payload === 'object' && 'success' in payload) {
      const wrapped = payload as ApiResponse<T>;
      if (!wrapped.success) {
        throw new Error(
          `API Error: ${wrapped.error || 'Unknown error'} (${wrapped.errorCode || 'UNKNOWN'})`
        );
      }
      return wrapped.data as T;
    }

    // Guard against null payloads slipping through as T
    if (payload === null) {
      throw new Error(`API returned unexpected empty response (${response.status})`);
    }
    return payload as T;
  }

  // Player endpoints
  async createPlayer(handle: string): Promise<CreatePlayerResponse> {
    const request: CreatePlayerRequest = { handle };
    const response = await this.request<CreatePlayerResponse>('/player/create', {
      method: 'POST',
      body: JSON.stringify(request),
    });
    this.sessionToken = response.sessionToken;
    return response;
  }

  async loadPlayer(operatorId: string): Promise<LoadPlayerResponse> {
    const request: LoadPlayerRequest = { operatorId };
    const response = await this.request<LoadPlayerResponse>('/player/load', {
      method: 'POST',
      body: JSON.stringify(request),
    });
    this.sessionToken = response.sessionToken;
    return response;
  }

  // Game endpoints
  async saveGame(gameState: GameState): Promise<SaveGameResponse> {
    if (!this.sessionToken) {
      throw new Error('No session token - must create or load player first');
    }

    const request: SaveGameRequest = {
      sessionToken: this.sessionToken,
      gameState,
    };

    return this.request<SaveGameResponse>('/game/save', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async completeGame(
    scenarioId: string,
    decisions: Decision[],
    playTimeSeconds: number,
    outcomeType: string,
    path: string,
    casualties?: number
  ): Promise<CompleteGameResponse> {
    if (!this.sessionToken) {
      throw new Error('No session token - must create or load player first');
    }

    const request: CompleteGameRequest = {
      sessionToken: this.sessionToken,
      scenarioId,
      decisions,
      playTimeSeconds,
      outcomeType,
      path,
      casualties,
    };

    return this.request<CompleteGameResponse>('/game/complete', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  // Leaderboard endpoints
  async getLeaderboard(options?: {
    page?: number;
    pageSize?: number;
    path?: string;
    outcome?: string;
    scenario?: string;
  }): Promise<LeaderboardResponse> {
    const params = new URLSearchParams();
    // Use explicit null checks so page=0 and pageSize=0 aren't silently dropped
    if (options?.page !== undefined && options.page !== null) {
      params.set('page', Math.max(1, options.page).toString());
    }
    if (options?.pageSize !== undefined && options.pageSize !== null) {
      params.set('pageSize', Math.max(1, options.pageSize).toString());
    }
    if (options?.path) params.set('path', options.path);
    if (options?.outcome) params.set('outcome', options.outcome);
    if (options?.scenario) params.set('scenario', options.scenario);

    const queryString = params.toString();
    const endpoint = `/leaderboard${queryString ? `?${queryString}` : ''}`;

    return this.request<LeaderboardResponse>(endpoint);
  }

  async getGlobalStats(): Promise<GlobalStatistics> {
    const response = await this.request<StatsResponse>('/stats');
    return response.global;
  }

  // ========================================
  // Campaign Management
  // ========================================

  /**
   * Create a new campaign
   * @param name Optional campaign name
   * @param mode Optional campaign mode ('chronological', 'free_play', 'new_game_plus')
   */
  async createCampaign(name?: string, mode?: string): Promise<CreateCampaignResponse> {
    if (!this.sessionToken) {
      throw new Error('No session token - must create or load player first');
    }

    const body: Record<string, string> = {};
    if (name) body.name = name;
    if (mode) body.mode = mode;

    const response = await this.request<CreateCampaignResponse>('/campaign/create', {
      method: 'POST',
      body: JSON.stringify(body),
    });

    // Update session token if a new one is provided
    if (response.sessionToken) {
      this.sessionToken = response.sessionToken;
    }

    return response;
  }

  /**
   * Get all active campaigns for the current player
   */
  async getActiveCampaigns(): Promise<Campaign[]> {
    if (!this.sessionToken) {
      throw new Error('No session token - must create or load player first');
    }

    return this.request<Campaign[]>('/campaign/active');
  }

  /**
   * Load an existing campaign by ID
   * @param campaignId The campaign UUID to load
   */
  async loadCampaign(campaignId: string): Promise<LoadCampaignResponse> {
    if (!this.sessionToken) {
      throw new Error('No session token - must create or load player first');
    }

    const response = await this.request<LoadCampaignResponse>(`/campaign/${campaignId}`);

    // Update session token if a new one is provided
    if (response.sessionToken) {
      this.sessionToken = response.sessionToken;
    }

    return response;
  }

  /**
   * Save campaign progress
   * @param data The save campaign request data
   */
  async saveCampaign(data: SaveCampaignRequest): Promise<SaveCampaignResponse> {
    if (!this.sessionToken) {
      throw new Error('No session token - must create or load player first');
    }

    return this.request<SaveCampaignResponse>('/campaign/save', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Complete a scenario within a campaign
   * @param data The scenario completion data
   */
  async completeCampaignScenario(data: CompleteScenarioRequest): Promise<CompleteScenarioResponse> {
    if (!this.sessionToken) {
      throw new Error('No session token - must create or load player first');
    }

    return this.request<CompleteScenarioResponse>('/campaign/complete', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ========================================
  // Checkpoint Management
  // ========================================

  /**
   * Create a checkpoint for the current campaign state
   * @param campaignToken The campaign UUID
   * @param name The checkpoint name
   * @param data The checkpoint data containing current state
   */
  async createCheckpoint(
    campaignToken: string,
    name: string,
    data: CheckpointData
  ): Promise<CheckpointResponse> {
    if (!this.sessionToken) {
      throw new Error('No session token - must create or load player first');
    }

    return this.request<CheckpointResponse>('/checkpoint/create', {
      method: 'POST',
      body: JSON.stringify({
        campaignUuid: campaignToken,
        name,
        ...data,
      }),
    });
  }

  /**
   * List all checkpoints for a campaign
   * @param campaignId The campaign UUID
   */
  async listCheckpoints(campaignId: string): Promise<CheckpointMetadata[]> {
    if (!this.sessionToken) {
      throw new Error('No session token - must create or load player first');
    }

    return this.request<CheckpointMetadata[]>(`/checkpoint/list/${campaignId}`);
  }

  /**
   * Restore a checkpoint
   * @param campaignId The campaign UUID
   * @param checkpointId The checkpoint UUID to restore
   */
  async restoreCheckpoint(
    campaignId: string,
    checkpointId: string
  ): Promise<RestoreCheckpointResponse> {
    if (!this.sessionToken) {
      throw new Error('No session token - must create or load player first');
    }

    const response = await this.request<RestoreCheckpointResponse>('/checkpoint/restore', {
      method: 'POST',
      body: JSON.stringify({
        campaignUuid: campaignId,
        checkpointUuid: checkpointId,
      }),
    });

    // Update session token if a new one is provided
    if (response.sessionToken) {
      this.sessionToken = response.sessionToken;
    }

    return response;
  }

  /**
   * Delete a checkpoint
   * @param checkpointId The checkpoint UUID to delete
   */
  async deleteCheckpoint(checkpointId: string): Promise<void> {
    if (!this.sessionToken) {
      throw new Error('No session token - must create or load player first');
    }

    await this.request<void>(`/checkpoint/${checkpointId}`, {
      method: 'DELETE',
    });
  }

  // ========================================
  // World State
  // ========================================

  /**
   * Get the current world state for a campaign
   * @param campaignId The campaign UUID
   */
  async getWorldState(campaignId: string): Promise<WorldState> {
    if (!this.sessionToken) {
      throw new Error('No session token - must create or load player first');
    }

    return this.request<WorldState>(`/worldstate/${campaignId}`);
  }

  /**
   * Get consequences triggered by a specific scenario in a campaign
   * @param campaignId The campaign UUID
   * @param scenarioId The scenario ID to get consequences for
   */
  async getConsequences(campaignId: string, scenarioId: string): Promise<ConsequenceData[]> {
    if (!this.sessionToken) {
      throw new Error('No session token - must create or load player first');
    }

    return this.request<ConsequenceData[]>(`/consequences/${campaignId}/${scenarioId}`);
  }
}

// Singleton instance
export const apiClient = new ApiClient();
export default apiClient;
